import React from 'react';
import { OverlayTrigger, Spinner, Table, Tooltip, headerTitle } from '@/components/ui';
import ServerPagination from './ServerPagination';
import './ReportPreviewTable.css';

interface Column<T> {
  label: string;
  key: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  headerTooltip?: string;
  headerContent?: React.ReactNode;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

interface ReportPreviewTableProps<T> {
  columns: Column<T>[];
  data: T[];
  pagination: Pagination;
  loading: boolean;
  emptyMessage?: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  tableHeight?: string; // New prop for fixed table height
  isFiltersCollapsed?: boolean; // New prop to know if filters are collapsed
  yellowHeaderColumn?: string; // New prop to specify which column should have yellow header
  onRowContextMenu?: (row: T, event: React.MouseEvent) => void; // New prop for context menu handler
  onRowClick?: (row: T) => void; // New prop for row click handler
  getRowStyle?: (row: T) => React.CSSProperties | undefined;
}

function ReportPreviewTable<T extends { id: number | string }>(props: ReportPreviewTableProps<T>) {
  const {
    columns,
    data,
    pagination,
    loading,
    emptyMessage,
    sortField,
    sortDirection,
    onSort,
    // `tableHeight` used to switch between a couple of viewport-relative pixel heights (see
    // git history) and a "100%" flex-fill mode used by exactly one caller. The pixel modes are
    // what caused "horizontal scroll only appears at the bottom of the page": `calc(100vh -
    // 350px)` doesn't know about the sidebar/filters/page chrome actually on screen, so on
    // anything other than a full desktop viewport this box was taller than the space its flex
    // ancestors gave it, and the real scrollbar ended up far below the fold. Every caller now
    // gets the flex-fill behaviour unconditionally, so the table's scroll region is always
    // exactly the space its parent hands it. The prop is kept (unused) so existing callers that
    // still pass tableHeight="100%" don't need to change.
    isFiltersCollapsed = false, // Default to false
    yellowHeaderColumn, // New prop for yellow header column
    onRowContextMenu, // New prop for context menu handler
    onRowClick, // New prop for row click handler
    getRowStyle
  } = props;
  void isFiltersCollapsed; // no longer changes the table height, kept for API compatibility

  const handleSort = (field: string) => {
    if (onSort) {
      onSort(field);
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up tw-text-content-muted tw-ml-1" style={{ fontSize: '0.75rem' }}></i>;
    }
    return sortDirection === 'asc'
      ? <i className="bi bi-arrow-up tw-text-primary tw-ml-1" style={{ fontSize: '0.75rem' }}></i>
      : <i className="bi bi-arrow-down tw-text-primary tw-ml-1" style={{ fontSize: '0.75rem' }}></i>;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
      {/* Table Container - fills whatever height/width its flex parent gives it and owns both
          scroll axes itself (single scroll region, no viewport-relative sizing). minWidth: 0 is
          required alongside flex: 1 - without it a flex item won't shrink below its content's
          natural width, so the wide table would push the *page* to scroll sideways instead of
          scrolling inside this box.

          Do not set height: 100% here: this box is a flex sibling of the pagination footer, so
          100% of the parent would steal the footer's space and clip page controls off-screen.
          flex: 1 + minHeight: 0 takes the leftover pane height and is what makes overflow: auto
          actually produce a vertical scrollbar. position: relative anchors the loading overlay. */}
      <div style={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflow: 'auto',
        position: 'relative',
        border: '1px solid #dee2e6',
        borderRadius: '0.375rem'
      }}>
        <Table
          striped
          bordered
          hover
          size="sm"
          className="report-preview-table"
          // width: max-content (not 100%) lets the table keep every column's specified width and
          // overflow the container on a narrow screen instead of squeezing all columns down to
          // illegible slivers - that's what makes the container's horizontal scrollbar meaningful.
          style={{ marginBottom: 0, tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}
        >
          <thead style={{ 
            position: 'sticky', 
            top: 0, 
            backgroundColor: '#ffffff', 
            zIndex: 10,
            borderBottom: '2px solid #dee2e6'
          }}>
            <tr>
              {columns.map((col) => {
                const headerInner = (
                  <div className="tw-flex tw-items-center">
                    {col.headerContent || <span>{col.label}</span>}
                    {col.sortable && getSortIcon(col.key)}
                  </div>
                );
                return (
                  <th
                    key={col.key}
                    style={{
                      backgroundColor: yellowHeaderColumn === col.key ? '#fff3cd' : '#ffffff',
                      width: col.width || 'auto',
                      cursor: col.sortable ? 'pointer' : col.headerTooltip ? 'help' : 'default',
                      userSelect: 'none',
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.875rem',
                      borderBottom: '2px solid #dee2e6'
                    }}
                    onClick={() => col.sortable && handleSort(col.key)}
                    className={col.sortable ? 'sortable-header' : ''}
                    // Headers are single-line and ellipsized (see report-preview-th-label / the
                    // Table.tsx header styles), so a label wider than its column just disappears.
                    // headerTooltip already gives a rich hover via OverlayTrigger; everything else
                    // gets the label back as a native title so it's still readable on hover.
                    title={col.headerTooltip ? undefined : (headerTitle(col.headerContent) ?? col.label)}
                  >
                    {col.headerTooltip ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`report-col-tip-${col.key}`} style={{ maxWidth: '320px' }}>
                            <span
                              style={{
                                whiteSpace: 'pre-line',
                                textAlign: 'left',
                                display: 'block'
                              }}
                            >
                              {col.headerTooltip}
                            </span>
                          </Tooltip>
                        }
                      >
                        <span style={{ cursor: 'help' }}>{headerInner}</span>
                      </OverlayTrigger>
                    ) : (
                      headerInner
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {(!data || data.length === 0) && !loading && (
              <tr>
                <td colSpan={columns.length} className="tw-text-center tw-py-3">
                  {emptyMessage || 'No data available.'}
                </td>
              </tr>
            )}
            {data && data.map((row) => (
              <tr 
                key={row.id} 
                style={{ 
                  height: '40px',
                  cursor: onRowClick ? 'pointer' : 'default',
                  ...(getRowStyle ? getRowStyle(row) : {})
                }}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onContextMenu={onRowContextMenu ? (e) => {
                  e.preventDefault();
                  onRowContextMenu(row, e);
                } : undefined}
                className={onRowClick ? 'table-row-clickable' : ''}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      width: col.width || 'auto',
                      padding: '0.375rem 0.75rem',
                      fontSize: '0.875rem',
                      verticalAlign: 'middle'
                    }}
                  >
                    {col.render ? col.render(row) : (row as any)[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2
            }}
          >
            <Spinner animation="border" />
          </div>
        )}
      </div>
      
      {/* Pagination stays outside the table scroll region so page controls remain visible
          after Run Report, even when the current page has many rows. */}
      {(pagination.totalPages > 1 || pagination.total > pagination.limit) && (
        <div
          className="p-2"
          style={{
            borderTop: '1px solid #dee2e6',
            backgroundColor: 'white',
            flexShrink: 0
          }}
        >
          <ServerPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.total}
            pageSize={pagination.limit}
            onPageChange={pagination.onPageChange}
            onPageSizeChange={pagination.onPageSizeChange || (() => {})}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
}

export default ReportPreviewTable; 